import { z } from 'zod';
import { type SingularityLevier, singularityLevierSchema } from './singularity-setup.js';

/**
 * Pure import of a "Setup de singularité" from Excel (spec
 * docs/specs/singularity-setup.md, section "Import depuis Excel").
 *
 * Both import paths (pasted cells and `.xlsx` file) go through this module so
 * they share one behaviour: header matching, row-to-step mapping, fusion with
 * the page draft, field limits and typed refusals.
 */

export type SetupImportField = Exclude<keyof SingularityLevier, 'order'>;
export type SetupImportColumnKey = SetupImportField | 'step';

export interface SetupImportColumn {
  key: SetupImportColumnKey;
  canonical: string;
  synonyms: readonly string[];
}

/** Step column first, then the 8 fields in the editor's order. */
export const SETUP_IMPORT_COLUMNS: readonly SetupImportColumn[] = [
  { key: 'step', canonical: 'Étape', synonyms: ['Etape', 'N°', 'Numéro', 'Ordre'] },
  { key: 'driver', canonical: 'Driver', synonyms: ['Drivers'] },
  {
    key: 'levierImplication',
    canonical: "Levier d'implication",
    synonyms: ['Levier implication'],
  },
  {
    key: 'detailImplication',
    canonical: "Détail de l'implication",
    synonyms: ['Détail implication'],
  },
  { key: 'principeAction', canonical: "Principe d'action", synonyms: ['Principe action'] },
  { key: 'methode', canonical: 'Méthode', synonyms: ['Méthodes'] },
  {
    key: 'levierValeur',
    canonical: 'Levier de valeur',
    synonyms: ['Levier valeur', 'Leviers de valeur'],
  },
  { key: 'detailValeur', canonical: 'Détail de la valeur', synonyms: ['Détail valeur'] },
  { key: 'effetProduit', canonical: 'Effet produit', synonyms: ['Effets produits', 'Effet'] },
];

const SETUP_IMPORT_FIELDS: readonly SetupImportField[] = [
  'driver',
  'levierImplication',
  'detailImplication',
  'principeAction',
  'methode',
  'levierValeur',
  'detailValeur',
  'effetProduit',
];

const STEP_COUNT = 6;
const STEPS: readonly number[] = [1, 2, 3, 4, 5, 6];

function schemaMaxLength(field: SetupImportField): number {
  const max = singularityLevierSchema.shape[field].unwrap().maxLength;
  if (max === null) {
    throw new Error(`singularityLevierSchema.${field} has no max length`);
  }
  return max;
}

/** Max length per field, read from `singularityLevierSchema`. */
export const SETUP_IMPORT_FIELD_MAX_LENGTH: Readonly<Record<SetupImportField, number>> = {
  driver: schemaMaxLength('driver'),
  levierImplication: schemaMaxLength('levierImplication'),
  detailImplication: schemaMaxLength('detailImplication'),
  principeAction: schemaMaxLength('principeAction'),
  methode: schemaMaxLength('methode'),
  levierValeur: schemaMaxLength('levierValeur'),
  detailValeur: schemaMaxLength('detailValeur'),
  effetProduit: schemaMaxLength('effetProduit'),
};

export const SETUP_IMPORT_MAX_BYTES = 5 * 1024 * 1024;
/** The backend reads at most this many rows per sheet. */
export const SETUP_IMPORT_MAX_ROWS = 200;
/** The backend reads at most this many columns per sheet. */
export const SETUP_IMPORT_MAX_COLUMNS = 40;
/** The header row of a file table must sit within the first N rows of its sheet. */
export const SETUP_IMPORT_HEADER_SCAN_ROWS = 20;

const ARTICLE_TOKENS: ReadonlySet<string> = new Set([
  'de',
  'du',
  'd',
  'la',
  'l',
  'le',
  'les',
  'des',
]);

function foldText(raw: string): string {
  return raw.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

/**
 * Header normalization: case, accents, whitespace, punctuation and French
 * articles are ignored. "Détail de l'implication" -> "detailimplication".
 */
export function normalizeSetupHeader(raw: string): string {
  return foldText(raw)
    .replace(/[^a-z0-9]+/g, ' ')
    .split(' ')
    .filter((token) => token.length > 0 && !ARTICLE_TOKENS.has(token))
    .join('');
}

const HEADER_LOOKUP: ReadonlyMap<string, SetupImportColumnKey> = new Map(
  SETUP_IMPORT_COLUMNS.flatMap((column) =>
    [column.canonical, ...column.synonyms].map(
      (label) => [normalizeSetupHeader(label), column.key] as const,
    ),
  ),
);

export function matchSetupHeader(raw: string): SetupImportColumnKey | null {
  const normalized = normalizeSetupHeader(raw);
  if (normalized.length === 0) return null;
  return HEADER_LOOKUP.get(normalized) ?? null;
}

/**
 * Parses the Excel clipboard format: rows separated by `\r\n` or `\n`, cells
 * by `\t`. A cell starting with `"` is quoted: it may contain tabs, newlines
 * and `""` (an escaped quote). An unterminated quote is kept literally. The
 * trailing empty line Excel appends is dropped.
 */
export function parseClipboardTable(text: string): string[][] {
  const rows: string[][] = [];
  if (text.length === 0) return rows;

  let row: string[] = [];
  let cell = '';
  let index = 0;
  let atCellStart = true;

  const endRow = (): void => {
    row.push(cell);
    rows.push(row);
    row = [];
    cell = '';
    atCellStart = true;
  };

  while (index < text.length) {
    const char = text.charAt(index);

    if (atCellStart && char === '"') {
      const quoted = readQuotedCell(text, index);
      // Excel only quotes cells holding a tab or line break, so the closing
      // quote must end the cell; otherwise `"Zéro défaut" garanti` is plain text.
      if (quoted !== null && endsCell(text, quoted.end)) {
        cell = quoted.value;
        index = quoted.end;
        atCellStart = false;
        continue;
      }
    }
    atCellStart = false;

    if (char === '\t') {
      row.push(cell);
      cell = '';
      atCellStart = true;
      index += 1;
    } else if (char === '\n') {
      endRow();
      index += 1;
    } else if (char === '\r' && text.charAt(index + 1) === '\n') {
      endRow();
      index += 2;
    } else {
      cell += char;
      index += 1;
    }
  }

  // Text not ending with a newline: flush the last row.
  if (!atCellStart || row.length > 0 || cell.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  return rows;
}

function endsCell(text: string, index: number): boolean {
  const char = text.charAt(index);
  return index >= text.length || char === '\t' || char === '\n' || char === '\r';
}

/** Reads a quoted cell starting at `start` (the opening quote). Null when never closed. */
function readQuotedCell(text: string, start: number): { value: string; end: number } | null {
  let value = '';
  let index = start + 1;
  while (index < text.length) {
    const char = text.charAt(index);
    if (char === '"') {
      if (text.charAt(index + 1) === '"') {
        value += '"';
        index += 2;
        continue;
      }
      return { value, end: index + 1 };
    }
    value += char;
    index += 1;
  }
  return null;
}

export interface SheetGrid {
  name: string;
  rows: string[][];
}

export const sheetGridSchema = z.object({
  name: z.string(),
  rows: z.array(z.array(z.string())),
});

/** Response of `POST /companies/{id}/singularity-setup/import`. */
export const importedWorkbookSchema = z.object({
  fileName: z.string(),
  sheets: z.array(sheetGridSchema),
});
export type ImportedWorkbook = z.infer<typeof importedWorkbookSchema>;

export interface DetectedSetupTable {
  sheetName: string;
  headerRowIndex: number;
  /** `table[0]` is the header row, then data rows up to (excluding) the first fully empty row. */
  table: string[][];
}

function isEmptyRow(row: readonly string[]): boolean {
  return row.every((cell) => cell.trim().length === 0);
}

function countRecognizedHeaders(row: readonly string[]): number {
  return row.filter((cell) => matchSetupHeader(cell) !== null).length;
}

/**
 * File path table detection: per sheet in workbook order, the header row is
 * the first row among the first `SETUP_IMPORT_HEADER_SCAN_ROWS` containing at
 * least 2 recognized headers (the step column counts). Returns every matching
 * sheet in workbook order.
 */
export function findSetupTables(sheets: readonly SheetGrid[]): DetectedSetupTable[] {
  const detected: DetectedSetupTable[] = [];
  for (const sheet of sheets) {
    const scanned = sheet.rows.slice(0, SETUP_IMPORT_HEADER_SCAN_ROWS);
    const headerRowIndex = scanned.findIndex((row) => countRecognizedHeaders(row) >= 2);
    if (headerRowIndex === -1) continue;

    const following = sheet.rows.slice(headerRowIndex + 1);
    const firstEmpty = following.findIndex(isEmptyRow);
    const dataRows = firstEmpty === -1 ? following : following.slice(0, firstEmpty);
    const header = sheet.rows[headerRowIndex] ?? [];
    detected.push({
      sheetName: sheet.name,
      headerRowIndex,
      table: [[...header], ...dataRows.map((row) => [...row])],
    });
  }
  return detected;
}

export type SetupImportRefusal =
  /** No recognized field column in the header row (step alone is not enough). */
  | { kind: 'no-header' }
  /** File: no sheet contains a setup table. */
  | { kind: 'no-table' }
  /** Two columns designate the same field; `headers` are their header texts. */
  | { kind: 'duplicate-column'; key: SetupImportColumnKey; headers: string[] }
  /** More than 6 non-empty data rows; labels = first non-empty cell of each row beyond the 6th. */
  | { kind: 'too-many-rows'; count: number; extraRowLabels: string[] }
  | { kind: 'invalid-step'; invalidValues: string[]; duplicateSteps: number[] };

export interface SetupImportWrite {
  order: number;
  field: SetupImportField;
  previous: string | null;
  next: string;
}

export interface SetupImportSkip {
  order: number;
  field: SetupImportField;
  length: number;
  limit: number;
  text: string;
}

export interface SetupImportPreview {
  /** The draft after fusion, 6 rows sorted by order. */
  merged: SingularityLevier[];
  /** Cells that change. */
  written: SetupImportWrite[];
  skippedTooLong: SetupImportSkip[];
  /** Fields with no column, in editor order. */
  missingColumns: SetupImportField[];
  /** Header texts of non-empty unrecognized columns. */
  ignoredColumns: string[];
  /** Steps with no data row. */
  missingSteps: number[];
  importedStepCount: number;
}

export type SetupImportOutcome =
  | { ok: true; preview: SetupImportPreview }
  | { ok: false; refusal: SetupImportRefusal };

function cellAt(row: readonly string[], index: number): string {
  return (row[index] ?? '').trim();
}

/** `N` or `Étape N` / `Etape N` (optional space), N in 1..6; null otherwise. */
function parseStep(raw: string): number | null {
  const match = /^(?:etape\s*)?([1-6])$/.exec(foldText(raw.trim()));
  return match ? Number(match[1]) : null;
}

function emptyLevier(order: number): SingularityLevier {
  return {
    order,
    driver: null,
    levierImplication: null,
    detailImplication: null,
    principeAction: null,
    methode: null,
    levierValeur: null,
    detailValeur: null,
    effetProduit: null,
  };
}

interface ColumnMapping {
  columns: Map<SetupImportColumnKey, number>;
  ignoredColumns: string[];
  duplicate: { key: SetupImportColumnKey; headers: string[] } | null;
}

function mapColumns(header: readonly string[]): ColumnMapping {
  const byKey = new Map<SetupImportColumnKey, number[]>();
  const ignoredColumns: string[] = [];
  header.forEach((raw, index) => {
    const text = raw.trim();
    if (text.length === 0) return;
    const key = matchSetupHeader(text);
    if (key === null) {
      ignoredColumns.push(text);
      return;
    }
    byKey.set(key, [...(byKey.get(key) ?? []), index]);
  });

  let duplicate: ColumnMapping['duplicate'] = null;
  const columns = new Map<SetupImportColumnKey, number>();
  for (const column of SETUP_IMPORT_COLUMNS) {
    const indexes = byKey.get(column.key);
    if (indexes === undefined) continue;
    const [first] = indexes;
    if (first !== undefined) columns.set(column.key, first);
    if (indexes.length > 1 && duplicate === null) {
      duplicate = { key: column.key, headers: indexes.map((index) => cellAt(header, index)) };
    }
  }
  return { columns, ignoredColumns, duplicate };
}

/**
 * Core import: `table[0]` is the header row, the rest are data rows. Fully
 * empty rows are ignored. Refusal checks run in order: duplicate column, no
 * header, too many rows, invalid step.
 */
export function importSetupTable(
  table: readonly (readonly string[])[],
  draft: readonly SingularityLevier[],
): SetupImportOutcome {
  const [header = [], ...rest] = table;
  const { columns, ignoredColumns, duplicate } = mapColumns(header);

  if (duplicate !== null) {
    return { ok: false, refusal: { kind: 'duplicate-column', ...duplicate } };
  }
  if (!SETUP_IMPORT_FIELDS.some((field) => columns.has(field))) {
    return { ok: false, refusal: { kind: 'no-header' } };
  }

  const dataRows = rest.filter((row) => !isEmptyRow(row));
  if (dataRows.length > STEP_COUNT) {
    return {
      ok: false,
      refusal: {
        kind: 'too-many-rows',
        count: dataRows.length,
        extraRowLabels: dataRows
          .slice(STEP_COUNT)
          .map((row) => row.map((cell) => cell.trim()).find((cell) => cell.length > 0) ?? ''),
      },
    };
  }

  const stepColumn = columns.get('step');
  let steps: number[];
  if (stepColumn === undefined) {
    steps = dataRows.map((_, index) => index + 1);
  } else {
    const invalidValues: string[] = [];
    const seen = new Set<number>();
    const duplicateSteps = new Set<number>();
    steps = [];
    for (const row of dataRows) {
      const raw = cellAt(row, stepColumn);
      const step = parseStep(raw);
      if (step === null) {
        invalidValues.push(raw);
        continue;
      }
      if (seen.has(step)) duplicateSteps.add(step);
      seen.add(step);
      steps.push(step);
    }
    if (invalidValues.length > 0 || duplicateSteps.size > 0) {
      return {
        ok: false,
        refusal: {
          kind: 'invalid-step',
          invalidValues,
          duplicateSteps: [...duplicateSteps].sort((a, b) => a - b),
        },
      };
    }
  }

  const merged = STEPS.map((order) => {
    const current = draft.find((levier) => levier.order === order);
    return current ? { ...current } : emptyLevier(order);
  });
  const written: SetupImportWrite[] = [];
  const skippedTooLong: SetupImportSkip[] = [];

  dataRows.forEach((row, rowIndex) => {
    const order = steps[rowIndex];
    const target = merged.find((levier) => levier.order === order);
    if (order === undefined || target === undefined) return;
    for (const field of SETUP_IMPORT_FIELDS) {
      const columnIndex = columns.get(field);
      if (columnIndex === undefined) continue;
      const next = cellAt(row, columnIndex);
      if (next.length === 0) continue;
      const previous = target[field];
      // Compared trimmed: imported cells are trimmed, so a saved value with
      // surrounding whitespace must still round-trip as "nothing to write".
      if (previous !== null && previous.trim() === next) continue;
      const limit = SETUP_IMPORT_FIELD_MAX_LENGTH[field];
      if (next.length > limit) {
        skippedTooLong.push({ order, field, length: next.length, limit, text: next });
        continue;
      }
      written.push({ order, field, previous, next });
      target[field] = next;
    }
  });

  const importedSteps = new Set(steps);
  written.sort((a, b) => a.order - b.order);
  skippedTooLong.sort((a, b) => a.order - b.order);

  return {
    ok: true,
    preview: {
      merged,
      written,
      skippedTooLong,
      missingColumns: SETUP_IMPORT_FIELDS.filter((field) => !columns.has(field)),
      ignoredColumns,
      missingSteps: STEPS.filter((step) => !importedSteps.has(step)),
      importedStepCount: importedSteps.size,
    },
  };
}

/**
 * Paste path: the first non-empty row of the pasted cells is the header row.
 * Empty input is refused as "no header".
 */
export function importSetupFromClipboard(
  text: string,
  draft: readonly SingularityLevier[],
): SetupImportOutcome {
  const rows = parseClipboardTable(text);
  const firstNonEmpty = rows.findIndex((row) => !isEmptyRow(row));
  if (firstNonEmpty === -1) return { ok: false, refusal: { kind: 'no-header' } };
  return importSetupTable(rows.slice(firstNonEmpty), draft);
}

/**
 * File path: detects the setup tables of the workbook, imports the one of
 * `sheetName` when given and matching, else the first. `candidateSheets`
 * feeds the sheet selector.
 */
export function importSetupFromWorkbook(
  workbook: { sheets: readonly SheetGrid[] },
  draft: readonly SingularityLevier[],
  sheetName?: string,
): SetupImportOutcome & { sheetName: string | null; candidateSheets: string[] } {
  const tables = findSetupTables(workbook.sheets);
  const candidateSheets = tables.map((table) => table.sheetName);
  const chosen = tables.find((table) => table.sheetName === sheetName) ?? tables[0];
  if (chosen === undefined) {
    return { ok: false, refusal: { kind: 'no-table' }, sheetName: null, candidateSheets };
  }
  return {
    ...importSetupTable(chosen.table, draft),
    sheetName: chosen.sheetName,
    candidateSheets,
  };
}
